import { useState } from 'react';
import { mdiDownload, mdiTrashCanOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Button, Col, Input, Label, Row, Table } from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { uploadDocument } from '../../../store/apis/travelRequestService';
import { initialValues, validationSchema } from './constant';
import { DeleteDocument } from './DeleteDocument';
import { FileUploadDropzone } from '../../Common';
import { downloadBlobType } from '../../../store/apis/commonService';
import { FileUtil } from '../../../utils';
import { getAuthUser } from '../../../utils/auth.util';
import eyeIcon from '../../../assets/icons/eyeicon.png';
import DisplayModal from '../../Common/DisplayModal/DisplayModal';
import { Messages } from '../../../common/messages';
import './UploadAttachments.scss';

function UploadAttachments({
  isView = false,
  isCommonOptionsLoading,
  commonOptions,
  detailsLoading,
  detailsData,
  requestNo,
  refetchTravelRequestDetails,
  isClone,
  id,
}) {
  const documentDetails = detailsData?.documentDetails;

  const [deleteDocumentModal, setDeleteDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState();

  const toggleDeleteDocumentModal = () => {
    setDeleteDocumentModal(!deleteDocumentModal);
  };
  const [isDisplayModalOpen, setDisplayModalOpen] = useState(false);
  const [displayModalFile, setDisplayModalFile] = useState(null);

  const {
    handleSubmit,
    control,
    reset,
    trigger,
    formState: { errors, isSubmitting },
    setValue,
    clearErrors,
    setError,
    watch,
  } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
  });

  const newAttachment = watch();

  const uploadDocumentMutation = useMutation({
    mutationFn: payload => uploadDocument(payload),
    onSuccess: () => {
      toast.success('Document uploaded successfully!', {
        className: 'toast-success',
      });
      reset();
      refetchTravelRequestDetails();
    },
    onError: error => {
      toast.error(error.message || 'Upload failed', {
        className: 'toast-error',
      });
    },
  });

  const onSubmit = data => {
    if (!requestNo) {
      toast.error(
        'Request number is not available. Please save the request first.',
        {
          className: 'toast-error',
        },
      );
      return;
    }

    const { file: files, documentType, comment } = newAttachment;

    files.forEach(file => {
      const formData = new FormData();
      formData.append('docType', 'DOC_TYPE_TRAVEL');
      formData.append('requestNo', requestNo || '');
      formData.append('docFileName', data.file.name);
      formData.append('docTypeCode', documentType);
      formData.append('comments', comment || '');
      formData.append('docIndex', 0);
      formData.append('file', file);
      uploadDocumentMutation.mutate(formData);
    });
  };

  const downloadFileMutation = useMutation({
    mutationFn: payload => downloadBlobType(payload),
  });

  const handleDownload = async (fileName, type, docIndex) => {
    try {
      let result = null;
      if (docIndex === -1) {
        result = await downloadFileMutation.mutateAsync({
          type: 'Passport_Travel_Request',
          hrCoCd: getAuthUser()?.hrCoCd,
          fileName,
        });
      } else {
        result = await downloadFileMutation.mutateAsync({
          refNo: id,
          type,
          fileName,
        });
      }

      FileUtil.downloadBlobFile(result, fileName || 'attachment');
    } catch (error) {
      toast.error('Failed to download file.', {
        className: 'toast-error',
      });
    }
  };

  const handleFileDisplay = async (fileName, type, docIndex) => {
    try {
      let result = null;

      if (docIndex === -1) {
        result = await downloadFileMutation.mutateAsync({
          type: 'Passport_Travel_Request',
          hrCoCd: getAuthUser()?.hrCoCd,
          fileName,
        });
      } else {
        result = await downloadFileMutation.mutateAsync({
          refNo: id,
          type,
          fileName,
        });
      }

      // Create a Blob URL
      const blob = new Blob([result], {
        type: result.type || 'application/pdf',
      });

      setDisplayModalFile(
        new File([blob], fileName, {
          type: blob.type,
        }),
      );

      // Store in state so the modal can use it
      setDisplayModalOpen(true);
    } catch (error) {
      toast.error('Failed to display file.', {
        className: 'toast-error',
      });
    }
  };

  const renderTableBody = () => {
    const tableData = documentDetails;
    // Ensure tableData is not undefined or null
    if (!tableData || Object.keys(tableData).length === 0) {
      if (detailsLoading) {
        return (
          <tr>
            <td className="text-center" colSpan={6}>
              Loading...
            </td>
          </tr>
        );
      }
      return (
        <tr>
          <td className="text-center" colSpan={6}>
            {Messages.NO_ATTACHMENTS}
          </td>
        </tr>
      );
    }

    return documentDetails?.map((doc, index) => (
      <tr key={index}>
        <td className="px-2 text-center">{index + 1}.</td>
        <td className="px-2" title={doc?.DocType}>
          {doc?.DocType || 'N/A'}
        </td>
        <td className="px-2" title={doc?.DocName}>
          {doc?.DocName || 'N/A'}
        </td>
        <td className="px-2">{doc?.UploadedBy || 'N/A'}</td>
        <td className="px-2">{doc?.UploadedDate}</td>
        <td className="px-2 d-flex justify-content-center align-items-center">
          {/* {!['15', '30', '90']?.includes(travelStatus) && ( */}
          <div className="d-flex gap-2 flex-row">
            <button
              type="button"
              className="btn btn-link p-0"
              title="View Document"
              onClick={() =>
                handleFileDisplay(doc.DocName, 'Travel_Request', doc.DocIndex)
              }
            >
              <img src={eyeIcon} alt="View" style={{ height: '15px' }} />
            </button>
            <button
              type="button"
              className="btn btn-link p-0"
              title="Download Document"
              onClick={() =>
                handleDownload(doc.DocName, 'Travel_Request', doc.DocIndex)
              }
            >
              <Icon path={mdiDownload} size={0.8} color="#017EBD" />
            </button>
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={() => {
                setSelectedDocument(doc);
                setDeleteDocumentModal(true);
              }}
              disabled={isView}
            >
              <Icon path={mdiTrashCanOutline} size={0.8} color="#D92D20" />
            </button>
          </div>
          {/* )} */}
        </td>
      </tr>
    ));
  };

  return (
    <>
      <div className="new-travel-request-card bg-white p-3" hidden={isClone}>
        <div className="d-flex flex-row align-items-center justify-content-between w-100 mb-2">
          <div className="fw-semibold fs-5">Upload Attachments</div>
          <Button
            type="button"
            className="custom-secondary-btn"
            disabled={isView}
            onClick={() => {
              const isValid = trigger();
              if (isValid) {
                handleSubmit(onSubmit)();
              }
            }}
          >
            {isSubmitting ? (
              <span>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                />
                Uploading...
              </span>
            ) : (
              'Add Document'
            )}
          </Button>
        </div>
        <Row>
          <Col xs={12} md={4} className="mb-3">
            <Label>
              Select Files<span className="text-danger">*</span>
            </Label>
            <Controller
              name="file"
              control={control}
              render={({ field }) => (
                <>
                  <FileUploadDropzone
                    multiple
                    error={errors.file}
                    selectedFiles={field.value}
                    onDrop={async acceptedFiles => {
                      try {
                        await validationSchema.fields.file.validate(
                          acceptedFiles,
                        );
                        clearErrors('files');
                        setValue('file', acceptedFiles, {
                          shouldValidate: true,
                        });
                      } catch (err) {
                        setError('file', {
                          type: 'manual',
                          message: err.message,
                        });
                      }
                    }}
                    className={errors.file ? 'is-invalid' : ''}
                    tooltipTitle="Upload supporting documents for this travel request"
                    handleDeleteFile={file => {
                      field.onChange(
                        field.value.filter(f => f.name !== file.name),
                      );
                      trigger();
                    }}
                    disabled={isView}
                  />
                  {errors.file && (
                    <span className="text-danger">{errors.file.message}</span>
                  )}
                </>
              )}
            />
          </Col>
          <Col xs={12} md={4} className="mb-3">
            <Label>
              Document Type<span className="text-danger">*</span>
            </Label>
            <Controller
              name="documentType"
              control={control}
              render={({ field }) => (
                <>
                  <Input
                    type="select"
                    className="form-select external-filter-input"
                    disabled={isCommonOptionsLoading || isView}
                    {...field}
                    title="Select the relevant type/category for this document from the dropdown options."
                  >
                    <option value="">Select Document Type</option>
                    {commonOptions?.travelDocumentTypes?.map(type => (
                      <option key={type.Code} value={type.Code}>
                        {type.Description}
                      </option>
                    ))}
                  </Input>
                  {errors.documentType && (
                    <span className="text-danger">
                      {errors.documentType.message}
                    </span>
                  )}
                </>
              )}
            />
          </Col>
          <Col xs={12} md={4} className="mb-3">
            <Label>Comments</Label>
            <Controller
              name="comment"
              control={control}
              render={({ field }) => (
                <Input
                  type="text"
                  className="form-control external-filter-input"
                  placeholder="Enter"
                  {...field}
                  title="Enter any  comments or clarifications for the document"
                  disabled={isView}
                />
              )}
            />
          </Col>
        </Row>
        <div className="table-responsive">
          <div className="ntr-table-container">
            <Table
              className="options-table align-middle text-nowrap ntr-table"
              id="tr-doc-table"
            >
              <thead>
                <tr>
                  <th className="text-center px-2">#</th>
                  <th className="px-2">Document Type</th>
                  <th className="px-2">Document Name</th>
                  <th className="px-2">Uploaded By</th>
                  <th className="px-2">Date</th>
                  <th className="px-2 text-center">Action</th>
                </tr>
              </thead>
              <tbody>{renderTableBody()}</tbody>
            </Table>
          </div>
        </div>
      </div>
      <DeleteDocument
        isOpen={deleteDocumentModal}
        toggle={toggleDeleteDocumentModal}
        document={selectedDocument}
        refetchTravelRequestDetails={
          refetchTravelRequestDetails && toggleDeleteDocumentModal
        }
      />
      <DisplayModal
        isModalOpen={isDisplayModalOpen}
        toggle={() => setDisplayModalOpen(false)}
        title="View Attachments"
        file={displayModalFile}
      />
    </>
  );
}

export default UploadAttachments;
